import toast from '@liquidweb-dev/undercurrent/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
	LWApiPublicAccountUserStateDataAddParamsRawI,
	LWApiPublicAccountUserStateDataAddResultRawI,
	LWApiPublicAccountUserStateDataListForKeyResultRawI,
} from '@liquidweb-dev/club-sauce/public/account/user/stateData/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';
import useSnackbarError from 'utility/hooks/useSnackbarError';
import { ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY } from './useListForKey';

export const useStatedataAdd = () => {
	const queryClient = useQueryClient();
	const snackbarError = useSnackbarError();

	return useMutation<
		LWApiPublicAccountUserStateDataAddResultRawI,
		ApiErrorI,
		LWApiPublicAccountUserStateDataAddParamsRawI
	>({
		mutationFn: (params) => api.account.user.stateData.apiRaw.add(params),

		onMutate: async (params) => {
			await queryClient.cancelQueries({
				queryKey: [
					ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
					{ key: params.key },
				],
			});

			queryClient.setQueryData<LWApiPublicAccountUserStateDataListForKeyResultRawI>(
				[ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY, { key: params.key }],
				(current) => {
					if (!current) {
						return current;
					}

					const data = JSON.parse(params.data);

					return {
						...current,
						data: [data, ...current.data],
					};
				},
			);
		},

		onSuccess: (_data, params) => {
			queryClient.invalidateQueries({
				queryKey: [
					ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
					{ key: params.key },
				],
			});
		},

		onError: snackbarError,
	});
};

export const useAddFavorite = () => {
	const queryClient = useQueryClient();
	const snackbarError = useSnackbarError();

	return useMutation<
		LWApiPublicAccountUserStateDataAddResultRawI,
		ApiErrorI,
		{ path: string; displayName: string }
	>({
		mutationFn: ({ path, displayName }) =>
			api.account.user.stateData.apiRaw.add({
				key: 'favorites',
				data: JSON.stringify({ path, displayName }),
			}),

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
					{ key: 'favorites' },
				],
			});

			toast.success('Added to favorites');
		},

		onError: snackbarError,
	});
};

export default useStatedataAdd;
